import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, CircularProgress, List, ListItem, ListItemText, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { doc, updateDoc, arrayUnion, onSnapshot, deleteDoc } from 'firebase/firestore';
import { firestore, auth } from '../../firebase';
import CommentInput from './CommentInput';
import ContentDisplay from './ContentDisplay';

function ItemModal({ open, handleClose, handleComplete, handleDelete, item }) {
  const [completed, setCompleted] = useState(item.completed);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (open) {
      const unsubscribe = onSnapshot(doc(firestore, 'items', item.id), (doc) => {
        if (doc.exists()) {
          setComments(doc.data().comments || []);
          setCompleted(doc.data().completed);
        }
      });

      return () => unsubscribe();
    }
  }, [open, item.id]);

  const handleCommentSubmit = async (comment) => {
    if (!currentUser) return;

    try {
      const itemRef = doc(firestore, 'items', item.id);
      await updateDoc(itemRef, {
        comments: arrayUnion({
          userId: currentUser.uid,
          displayName: currentUser.displayName || 'Anonymous User',
          content: comment,
          createdAt: new Date()
        })
      });
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleCompleteItem = async () => {
    if (currentUser && currentUser.uid === item.createdBy) {
      setLoading(true);
      try {
        const itemRef = doc(firestore, 'items', item.id);
        await updateDoc(itemRef, {
          completed: true
        });
        setCompleted(true);
        handleComplete(item.id);
      } catch (error) {
        console.error('Error completing item:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteConfirm = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteItem = async () => {
    if (currentUser && currentUser.uid === item.createdBy) {
      setLoading(true);
      try {
        const itemRef = doc(firestore, 'items', item.id);
        await deleteDoc(itemRef);
        handleDelete(item.id);
        handleClose();
      } catch (error) {
        console.error('Error deleting item:', error);
      } finally {
        setLoading(false);
        setDeleteConfirmOpen(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="item-dialog-title" fullWidth maxWidth="md">
        <DialogTitle id="item-dialog-title">
          {item.title}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>가격: {item.gold} gold</Typography>
          <Typography variant="subtitle1" gutterBottom>BattlenetID: {item.gameId}</Typography>

          <ContentDisplay content={item.content} />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6">댓글</Typography>
          <List>
            {comments.map((comment, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={comment.content}
                  secondary={`${comment.displayName} - ${new Date(comment.createdAt.toDate()).toLocaleString()}`}
                />
              </ListItem>
            ))}
          </List>

          <CommentInput onSubmit={handleCommentSubmit} currentUser={currentUser} />
        </DialogContent>
        <DialogActions>
          {currentUser && currentUser.uid === item.createdBy && (
            <>
              <Button
                onClick={handleCompleteItem}
                color="primary"
                disabled={completed || loading}
              >
                판매 종료
                {loading && <CircularProgress size={24} sx={{ml: 1}} />}
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                startIcon={<DeleteIcon />}
                disabled={loading}
              >
                삭제
              </Button>
            </>
          )}
          <Button onClick={handleClose} color="primary">
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"게시글을 삭제하시겠습니까?"}
        </DialogTitle>
        <DialogContent>
          <Typography id="alert-dialog-description">
            이 작업은 되돌릴 수 없습니다. 게시글을 삭제하면 모든 댓글도 함께 삭제됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            취소
          </Button>
          <Button onClick={handleDeleteItem} color="error" autoFocus>
            삭제
            {loading && <CircularProgress size={24} sx={{ml: 1}} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ItemModal;
